import { useCallback, useEffect, useState, MouseEvent } from 'react';

import { useNotificationList } from 'api/requests';
import { useUser } from 'libs/providers';

const REFETCH_INTERVAL = 3000;
const LIMIT = 10;

export const useNotifications = () => {
  const { user } = useUser();

  const [page, setPage] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openPopover = Boolean(anchorEl);

  const {
    loading,
    unreadCount = 0,
    refetch,
    listData,
  } = useNotificationList({
    pagination: {
      page,
      limit: LIMIT,
    },
    params: {},
  });

  useEffect(() => {
    if (user?.id && !openPopover) {
      const interval = setInterval(
        () => refetch({ page: 1 }),
        REFETCH_INTERVAL
      );
      return () => clearInterval(interval);
    }
    return undefined;
  }, [openPopover, refetch, user?.id]);

  useEffect(() => {
    if (!openPopover) {
      setPage(0);
    }
  }, [openPopover, refetch, user?.id]);

  const onLoadMore = useCallback(() => {
    if (listData && listData.list.length !== listData.total) {
      setPage((p) => p + 1);
    }
  }, [listData]);

  const onOpenPopover = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onClosePopover = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return {
    isNewNotifications: unreadCount > 0,
    loading,
    listData,
    hasMore: listData ? listData.list.length < listData.total : false,
    openPopover,
    onLoadMore,
    onOpenPopover,
    onClosePopover,
    anchorEl,
  };
};
